import { Component, EventEmitter, OnDestroy, OnInit } from '@angular/core';
import { ProjectCommunicationService } from '../../../communication/project-comms.service';
import { ConfiguredToastrService } from '../../../helper/toastr/configured-toastr.service';
import { take, takeUntil, takeWhile } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { AnessaTranslationInterface } from '../../../models/interfaces/anessa-translation.interface';
import { AnessaTranslationService } from '../../../helper/translation/anessa-translation.service';
import { UserpilotReloader } from '../../../helper/userpilot-reloader.component';
import { ScenarioModifyDialogComponent } from './scenario-modify-dialog/scenario-modify-dialog.component';
import { Scenario } from '../../../models/results/scenario-comparison/scenario.model';
import { ScenarioComparisonService } from '../scenario-comparison.service';
import { ProjectInputValidator } from '../../../helper/comparison-input-validator/project-input.validator';
import { Project } from '../../../models/project/project.model';
import { AffectedScenarioInput } from '../../../models/results/scenario-comparison/affected-scenario-input.model';
import { ScenarioConfirmationResponse, SimulatorScenarioConfirmationPopup } from '../../../helper/scenario-comparison/simulator-scenario-confirmation-popup/simulator-scenario-confirmation-popup.component';
import { CommunicationService } from '../../../communication/communication.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-scenario-list',
  templateUrl: './scenario-list.component.html',
  styleUrls: ['./scenario-list.component.scss']
})
export class ScenarioListComponent extends UserpilotReloader implements OnInit, OnDestroy, AnessaTranslationInterface {
  // used to clean up subscriptions when the component is destroyed
  onDestroyNotifier = new EventEmitter();
  // the array of current scenarios
  scenarios: Scenario[] = [];
  currentProject: Project;
  resultStateProject?: Project;

  // translation language change trigger
  trigger?: string;
  scenarioComparisonServiceReady = false;
  projectResultStateFetched = false;

  progressSubscription?: Subscription;

  invalidScenariosMap?: Map<string, string[]>;
  affectedScenariosMap?: Map<string, AffectedScenarioInput[]>;
  invalidScenariosList: string[];
  affectedScenariosList: string[];
  loadingScenariosList: string[] = [];

  constructor(
    private scenarioComparisonService: ScenarioComparisonService,
    private projectCommService: ProjectCommunicationService,
    private communicationService: CommunicationService,
    private toastrService: ConfiguredToastrService,
    private translationService: AnessaTranslationService,
    private matDialog: MatDialog,
    private dialog: MatDialog
  ) {
    super();
  }

  /**
   * Contains subscriptions for scenarios being loaded and updated, as well as
   * the translation language change notifier
   */
  ngOnInit() {
    this.scenarioComparisonService.serviceReadyNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .pipe(takeWhile(x => !x, true))
      .subscribe(loaded => {
        this.scenarioComparisonServiceReady = loaded;
        if (loaded) {
          this.scenarios = this.scenarioComparisonService.getScenarios();
        }
        this.setup();
      });

    this.scenarioComparisonService.updateNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe(() => {
        this.scenarios = this.scenarioComparisonService.getScenarios();
        // scenario comparison update notifier relies on project saved notifier to this is safe
        this.currentProject = this.projectCommService.getProject();
        this.setup();
      });

    this.projectCommService.resultsLoadedNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .pipe(takeWhile(x => !x, true))
      .subscribe(loaded => {
        if (loaded) {
          // get latest results to have access to the latest result project state
          this.projectCommService.fetchLatestResultSet()
            .pipe(take(1))
            .subscribe(async result => {
              // TODO is it safe to assume the project is ready at this point?
              this.currentProject = this.projectCommService.getProject();
              if (!result || !result['project-state']) {
                this.toastrService.error(this.translationService.get('SCENARIO_COMPARISON.ENTRY.FETCH_FAILED'));
              }
              this.resultStateProject = result['project-state'];
              this.projectResultStateFetched = true;
              this.setup();
            });
        }
      });

    this.progressSubscription = this.communicationService.progressNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe(msg => {
        if (!msg) return;
        if (msg.type === 'scenarios' && !!msg.message) {
          this.loadingScenariosList = this.loadingScenariosList.filter(scenarioName => scenarioName !== msg.message);
        }
      });

    this.translationService.languageChangeNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe(language => {
        this.trigger = language;
      });
  }

  /**
   * Triggers the onDestroyNotifier to clean up subscriptions and closes the emitter.
   */
  ngOnDestroy() {
    this.onDestroyNotifier.emit();
    this.onDestroyNotifier.unsubscribe();
    this.progressSubscription.unsubscribe();
  }

  /**
   * When the add scenario button is clicked calls the function to open the scenario modify
   * popup with a new scenario loaded.
   */
  onAddClick() {
    this.openScenarioModify();
  }

  /**
   * Opens the scenario modify popup to edit either a new scenario of the provided scenario.
   * If a scenario is being edited the index of the current scenario is also passed to the popup
   * to be used for a duplicate scenario name check. Save is performed with the popup
   * @param scenario if provided is a Scenario that will be passed to the scenario modify
   * popup to be edited.
   * @param index is the index of the provided scenario; used with the duplicate scenario name check
   */
  openScenarioModify(scenario?: Scenario, index?: number): void {
    let affectedInputs: string[] = [];
    let invalidInputs: string[] = [];

    if (scenario) {
      affectedInputs = this.getAffectedInputsForScenario(scenario.name);
      invalidInputs = this.getInvalidInputsForScenario(scenario.name);
    }

    this.matDialog.open(ScenarioModifyDialogComponent, {
      data: {
        scenario: scenario,
        editIndex: index,
        invalidInputs: invalidInputs,
        affectedInputs: affectedInputs
      },
      width: '90vw',
      height: '90vh',
      disableClose: true,
      autoFocus: false
    });
  }

  private getAffectedInputsForScenario(scenarioName: string): string[] {
    if (this.affectedScenariosMap?.has(scenarioName)) {
      const affectedInputs: AffectedScenarioInput[] = this.affectedScenariosMap.get(scenarioName);
      return affectedInputs.map(input => input.name);
    }
  }

  private getInvalidInputsForScenario(scenarioName: string): string[] {
    if (this.invalidScenariosMap?.has(scenarioName)) {
      return this.invalidScenariosMap.get(scenarioName);
    }
    return [];
  }

  /**
   * Scenario simulation compute click. Fetches the latest results and performs validation checks on
   * all scenarios before triggering the scenario simulation.
   */
  async onComputeClick() {

    // if anything is invalid or affected ask the user to confirm and decide what to do
    if (this.invalidScenariosMap.size > 0 || this.affectedScenariosMap.size > 0) {
      const dialogRef = this.dialog.open(SimulatorScenarioConfirmationPopup,
            {
              width: '70%',
              maxWidth: '1000px',
              maxHeight: '80%',
              panelClass: 'mat-dialog-overflow-auto',
              data: {
                invalidScenariosMap: this.invalidScenariosMap,
                affectedScenariosMap: this.affectedScenariosMap,
                isScenarioSimulation: true
              },
              disableClose: true
            });

      dialogRef.afterOpened().subscribe(() => {
        setTimeout(() => {
          const dialogPanel = document.querySelector('.cdk-overlay-pane.mat-mdc-dialog-panel');
          if (dialogPanel) {
            dialogPanel.scrollTop = 0;
          }
        }, 0);
      });

          dialogRef.afterClosed()
            .pipe(take(1))
            .pipe(takeUntil(this.onDestroyNotifier))
            .subscribe((res: ScenarioConfirmationResponse) => {
              if (res === ScenarioConfirmationResponse.ACKNOWLEDGE_AND_DELETE
                || res === ScenarioConfirmationResponse.ACKNOWLEDGE
              ) {
                this.projectCommService.projectSavedNotifier
                  .pipe(takeWhile(x => !x, true))
                  .pipe(takeUntil(this.onDestroyNotifier))
                  .subscribe((saved: boolean) => {
                    if (saved) {
                      this.loadingScenariosList = this.scenarios.map(s => s.name);
                      this.scenarioComparisonService.simulateScenarios();
                    }
                  });
                this.scenarioComparisonService.processAffectedAndInvalidScenarios(
                  this.affectedScenariosMap, this.invalidScenariosList);

              } else if (res === ScenarioConfirmationResponse.ACKNOWLEDGE_AND_KEEP) {
                this.scenarioComparisonService.processAffectedScenarios(
                  this.affectedScenariosMap
                );
              } else {
                this.toastrService.warning(this.translationService.get('SCENARIO_COMPARISON.SIMULATION.CANCELED'));
                return;
              }
            });

    } else {
      this.toastrService.info(
        this.translationService.get('SCENARIO_COMPARISON.ENTRY.COMPUTING_SCENARIOS')
      );
      this.loadingScenariosList = this.scenarios.map(s => s.name);
      this.scenarioComparisonService.simulateScenarios();
    }
  }


  /**
   * Opens the selected scenario in the scenario modify popup using the provided index.
   * @param index is the index of the selected scenario for edit.
   */
  onEditClick(index: number) {
    if (index >= 0 && index < this.scenarios?.length) {
      const scenario: Scenario = this.scenarios[index];
      this.openScenarioModify(scenario.deepCopy(), index);
    }
  }

  /**
   * Deletes the scenario at the provided index.
   * @param event is the number of the selected scenario for deletion.
   */
  onRemoveClick(event: number) {
    this.scenarioComparisonService.deleteScenario(event);
  }

  /**
   * Opens a copy of the selected scenario in the scenario modify popup. All data
   * for the selected scenario is transferred to the copy except the name.
   * @param index is the index of the selected scenario to be duplicated.
   */
  duplicateScenario(index: number) {
    if (index >= 0 && index < this.scenarios?.length) {
      const scenario = this.scenarios[index];
      if (!scenario) throw Error('unable to duplicate scenario');
      this.openScenarioModify(scenario.duplicate());
    }
  }

  private setup() {
    if (this.projectResultStateFetched && this.scenarioComparisonServiceReady) {
      this.invalidScenariosMap = ProjectInputValidator.getInvalidScenarioInputs(
        this.currentProject,
        this.resultStateProject
        );
      this.affectedScenariosMap = ProjectInputValidator.getAffectedInputs(
        this.scenarios, this.scenarioComparisonService.getLatestResultStateProjectInputs()
      );
      if (this.invalidScenariosMap) {
        this.invalidScenariosList = [... this.invalidScenariosMap.keys()];
      } else {
        this.invalidScenariosList = [];
      }
      if (this.affectedScenariosMap) {
        this.affectedScenariosList = [... this.affectedScenariosMap.keys()];
      } else {
        this.affectedScenariosList = [];
      }
    }
  }
}
